import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRight, ChevronDown, Info, AlertTriangle } from 'lucide-react';
import { incentiveAPI } from '../../services/api';
import { SearchInput } from '../../components/SearchInput';
import PaginationFooter from '../../components/PaginationFooter';
import { usePagination } from '../../hooks/usePagination';
import { PageErrorBanner } from '../../components/PageErrorBanner';
import { BlockLoader } from '../../components/loading';
import SalesCasesModal from '../../components/SalesCasesModal';
import NoLongerDeployedBadge from '../../components/NoLongerDeployedBadge';
import { formatIsoDay } from '../../utils/dateHelpers';
import {
  formatMoney, formatMultiplier, formatNumber, formatStoreDays, shortMonth,
  type IncentivePlanPart, type IncentiveRun, type IncentiveRunRow, type PerUnitPayout, type ProductPayout,
} from './types';

/**
 * What everyone earns this month.
 *
 * ── EVERY FIGURE OPENS ───────────────────────────────────────────────────────
 * An incentive number that cannot be explained is a number that gets argued
 * about. So each row expands into the per-product working: the target, what was
 * sold, how many sales the rules dropped, the achievement that survived, which
 * band it landed in and what that cell pays. A promoter asking "why is mine
 * lower than hers" is answered on screen rather than in a spreadsheet.
 *
 * A row paid under a per-unit plan has no target or band to explain. It shows the
 * units counted and what they paid instead, and opens into the units per product
 * and the ladder step by step — the engine's own sum, never re-worked here.
 *
 * Nothing here is stored. The figures are recalculated from the plan, the targets
 * and the sales as they stand right now — correcting a sale corrects the payout
 * without anyone remembering to re-run anything.
 *
 * ── THE MONTH'S STORES, NOT TODAY'S ──────────────────────────────────────────
 * Store lists every store the promoter worked at THAT month with its days, as the
 * Results tab writes them, and the plan is chosen from those stores. A month whose
 * days fell under two plans (a Mac store, then a Non Mac one) is paid by its days
 * under each: the Plan column names both with their days, and the opened row shows
 * each plan's working under its own heading.
 */

/** The New joiner pill's hover, for somebody who left in the month. The note in the opened row says the amount. */
const guaranteeLostHover = (r: IncentiveRunRow): string =>
  `${r.deployedUntil ? `Left on ${formatIsoDay(r.deployedUntil)}` : 'Left'}, in this month, so the new-joiner guarantee does not apply.`;

/** The month's stores with their days; a response from before they were sent shows its one store. */
const storesOf = (r: IncentiveRunRow): string =>
  r.monthStores && r.monthStores.length > 0 ? formatStoreDays(r.monthStores) : (r.storeCode ?? '—');

interface Props {
  projectId: number;
  month: string;
  planCount: number;
}

const PAGE_SIZES = [
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
];

function Tile({ label, value, hint, tone = 'plain' }: {
  label: string; value: string; hint?: string; tone?: 'plain' | 'good' | 'warn';
}) {
  const border = tone === 'good' ? 'border-green-200 bg-green-50'
    : tone === 'warn' ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white';
  return (
    <div className={`rounded-xl border px-4 py-3 ${border}`}>
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

type Outcome = NonNullable<ProductPayout['outcome']>;

/** The engine's verdict, or — for a response from before it sent one — the same verdict read off the other fields. */
const outcomeOf = (p: ProductPayout): Outcome => {
  if (p.outcome) return p.outcome;
  if (p.amount > 0) return 'paid';
  if (p.achievementPct === null) return 'no_target';
  if (p.bandLabel) return 'band_pays_nothing';
  return /below the|is required/.test(p.note ?? '') ? 'below_minimum' : 'no_band';
};

/**
 * The working, on the row itself, in the order the plan applies it:
 *   1. achievement — every sale against target — which picks the slab;
 *   2. where a sale rule (a date gap) applies, the share of sales that passed it,
 *      which a minimum rule checks;
 *   3. the slab and its amount, or what stopped it.
 * The full table still opens below the row; this is what an admin reads to know
 * WHY without opening it.
 *
 * Products with a target and nothing sold are folded into one line. They say the
 * same thing on every row, and six of them would bury the one that decided the pay.
 */
function ProductVerdicts({ products }: { products: ProductPayout[] }) {
  if (products.length === 0) return <span className="text-xs text-gray-400">No grid yet</span>;
  const shown = products.filter((p) => p.sold > 0 || p.amount > 0);
  const unsold = products.filter((p) => p.sold === 0 && p.amount === 0);

  return (
    <div className="space-y-1">
      {shown.map((p) => {
        const outcome = outcomeOf(p);
        const onPassRate = p.minimumOn === 'pass_rate';
        return (
          <div
            key={`${p.coord.productId}-${p.coord.skuId ?? ''}`}
            className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs"
          >
            <span className="font-medium text-gray-900">{p.label}</span>
            {outcome === 'counted' ? (
              <span className="text-gray-600">{formatNumber(p.qualifyingSold)} counted</span>
            ) : p.achievementPct === null ? (
              <span className="text-gray-500">{formatNumber(p.sold)} sold, no target set</span>
            ) : (
              <span className="text-gray-600" title="All sales ÷ target — this picks the slab">
                {formatNumber(p.sold)}/{formatNumber(p.target)} ={' '}
                <strong className="text-gray-900">{p.achievementPct}%</strong>
              </span>
            )}
            {p.passRatePct != null && (
              <span
                className={outcome === 'below_minimum' && onPassRate ? 'text-amber-700' : 'text-gray-600'}
                title="Sales that passed the date rule ÷ all sales"
              >
                · {formatNumber(p.qualifyingSold)}/{formatNumber(p.sold)} ={' '}
                <strong>{p.passRatePct}%</strong> passed the rule
                {p.minimumPct != null && onPassRate && ` (needs ${p.minimumPct}%)`}
              </span>
            )}
            {outcome === 'paid' && (
              <span className="rounded-full bg-green-100 px-2 py-0.5 font-medium text-green-800">
                {p.bandLabel} slab · {formatMoney(p.amount)}
              </span>
            )}
            {outcome === 'below_minimum' && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800">
                {onPassRate
                  ? 'Too few sales passed · ₹0'
                  : `Below the ${p.minimumPct != null ? `${p.minimumPct}% ` : ''}minimum · ₹0`}
              </span>
            )}
            {outcome === 'band_pays_nothing' && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
                {p.bandLabel} slab pays nothing
              </span>
            )}
            {outcome === 'no_band' && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800">
                No slab covers this · ₹0
              </span>
            )}
          </div>
        );
      })}
      {unsold.length > 0 && (
        <div className="text-xs text-gray-400">
          Nothing sold: {unsold.map((p) => p.label).join(', ')}
        </div>
      )}
    </div>
  );
}

/**
 * A per-unit row's verdict, on the row: units counted and what they paid, or what
 * stopped them paying.
 */
function PerUnitVerdict({ perUnit: u }: { perUnit: PerUnitPayout }) {
  const units = `${formatNumber(u.units)} unit${u.units === 1 ? '' : 's'}`;
  const amber = 'rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800';
  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs">
      {u.outcome === 'paid' && (
        <span className="rounded-full bg-green-100 px-2 py-0.5 font-medium text-green-800">
          {units} · {formatMoney(u.amount)}
        </span>
      )}
      {u.outcome === 'below_minimum' && (
        <span className={amber}>
          {units} · pays from {formatNumber(u.minUnits)} · ₹0
        </span>
      )}
      {u.outcome === 'nothing_counted' && (
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
          No units counted
        </span>
      )}
      {u.outcome === 'no_rates' && <span className={amber}>No rates set · ₹0</span>}
      {u.outcome === 'rates_pay_nothing' && <span className={amber}>Rates pay ₹0</span>}
      {u.excludedUnits > 0 && (
        <span className="text-amber-700" title="Sold, but failed the plan's sale rules">
          {formatNumber(u.excludedUnits)} not counted
        </span>
      )}
    </div>
  );
}

/**
 * "× 1.3 (Senior iPro)" — the designation multiplier, on the row, where it changed
 * the pay. The plan's own chips beside it are what the plan paid; Earned is after.
 */
function MultiplierNote({ row }: { row: IncentiveRunRow }) {
  if (row.multiplier === undefined || row.multiplier === 1) return null;
  const before = row.earnedBeforeMultiplier ?? row.earned;
  return (
    <span
      className="mt-1 inline-block rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-800"
      title={`${formatMoney(before)} from the plan ${formatMultiplier(row.multiplier)} = ${formatMoney(row.earned)}`}
    >
      × {formatMultiplier(row.multiplier).slice(1)} ({row.designation ?? 'designation'})
    </span>
  );
}

/** A rate as set, paise kept: ₹12.5 a unit is not ₹13 a unit. */
const formatRate = (n: number): string => `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/** "1–22", "31 and above". */
const stepRange = (fromUnit: number, toUnit: number | null): string =>
  toUnit === null ? `${formatNumber(fromUnit)} and above` : `${formatNumber(fromUnit)}–${formatNumber(toUnit)}`;

/**
 * A per-unit row, opened: the engine's sentence for the sum, the units each
 * product added, and the ladder step by step — every step, so the next one is in
 * sight. Products with nothing sold fold into one line, as on a grid row; an
 * "all products" plan lists every product in the project.
 */
function PerUnitWorking({ products, perUnit: u, onSeeCases }: {
  products: ProductPayout[]; perUnit: PerUnitPayout; onSeeCases: (p: ProductPayout) => void;
}) {
  const shown = products.filter((p) => p.sold > 0 || p.casesTotal > 0);
  const unsold = products.filter((p) => p.sold === 0 && p.casesTotal === 0);
  const stepUnits = u.tiers.reduce((n, t) => n + t.units, 0);
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-800">{u.working}</p>
      <div className="flex flex-wrap items-start gap-3">
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-3 py-1.5">Product</th>
                <th className="px-3 py-1.5 text-right">Sold</th>
                <th className="px-3 py-1.5 text-right" title="Sold, less any sale that failed the plan's sale rules">Counted</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shown.map((p) => (
                <tr key={`${p.coord.productId}-${p.coord.skuId ?? ''}`}>
                  <td className="px-3 py-1.5 font-medium text-gray-800">{p.label}</td>
                  <td className="px-3 py-1.5 text-right">{formatNumber(p.sold)}</td>
                  <td className="px-3 py-1.5 text-right">
                    {formatNumber(p.qualifyingSold)}
                    {p.casesExcluded > 0 && (
                      <span className="ml-1 text-amber-700">
                        · {p.casesExcluded} of {p.casesTotal} failed
                      </span>
                    )}
                    {p.casesTotal > 0 && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onSeeCases(p); }}
                        className="ml-2 font-medium text-primary-600 underline underline-offset-2"
                      >
                        see which
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-2 text-gray-500">Nothing sold of the products counted.</td>
                </tr>
              )}
            </tbody>
            <tfoot className="border-t border-gray-200 bg-gray-50 font-semibold text-gray-800">
              <tr>
                <td className="px-3 py-1.5">Total</td>
                <td className="px-3 py-1.5 text-right">{formatNumber(u.soldUnits)}</td>
                <td className="px-3 py-1.5 text-right">{formatNumber(u.units)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {u.tiers.length === 0 ? (
          <p className="text-xs text-amber-700">This plan has no rates set.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-left text-gray-500">
                <tr>
                  <th className="px-3 py-1.5">Units from–to</th>
                  <th className="px-3 py-1.5 text-right">₹ per unit</th>
                  <th className="px-3 py-1.5 text-right">Units</th>
                  <th className="px-3 py-1.5 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {u.tiers.map((t) => (
                  <tr key={t.fromUnit} className={t.units > 0 ? 'text-gray-800' : 'text-gray-400'}>
                    <td className="px-3 py-1.5">{stepRange(t.fromUnit, t.toUnit)}</td>
                    <td className="px-3 py-1.5 text-right">{formatRate(t.rate)}</td>
                    <td className="px-3 py-1.5 text-right">{formatNumber(t.units)}</td>
                    {/* Paise kept, so the steps add up to the total before its one rounding. */}
                    <td className="px-3 py-1.5 text-right">{t.units > 0 ? formatRate(t.amount) : '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-gray-200 bg-gray-50 font-semibold text-gray-800">
                <tr>
                  <td className="px-3 py-1.5">
                    Total
                    {u.minUnits !== null && u.minUnits > 0 && (
                      <span className="font-normal text-gray-500">{` · pays from ${formatNumber(u.minUnits)}`}</span>
                    )}
                  </td>
                  <td />
                  <td className="px-3 py-1.5 text-right">{formatNumber(stepUnits)}</td>
                  <td className="px-3 py-1.5 text-right">{formatMoney(u.amount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
      {unsold.length > 0 && (
        <p className="text-xs text-gray-400">Nothing sold: {unsold.map((p) => p.label).join(', ')}</p>
      )}
    </div>
  );
}

/**
 * A grid plan, opened: per product, the target, what was sold, the achievement
 * that picked the slab, the share of sales that passed a sale rule, and the cell.
 */
function GridWorking({ products, onSeeCases }: {
  products: ProductPayout[]; onSeeCases: (p: ProductPayout) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50 text-left text-gray-500">
          <tr>
            <th className="px-3 py-1.5">Product</th>
            <th className="px-3 py-1.5 text-right">Target</th>
            <th className="px-3 py-1.5 text-right">Sold</th>
            <th className="px-3 py-1.5 text-right" title="All sales ÷ target — picks the slab">Achieved</th>
            <th className="px-3 py-1.5 text-right" title="Sales that passed the date rule ÷ all sales">Passed the rule</th>
            <th className="px-3 py-1.5">Band</th>
            <th className="px-3 py-1.5 text-right">Pays</th>
            <th className="px-3 py-1.5">Why</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {products.map((p) => (
            <tr key={`${p.coord.productId}-${p.coord.skuId ?? ''}`}>
              <td className="px-3 py-1.5 font-medium text-gray-800">{p.label}</td>
              <td className="px-3 py-1.5 text-right">{formatNumber(p.target)}</td>
              <td className="px-3 py-1.5 text-right">{formatNumber(p.sold)}</td>
              <td className="px-3 py-1.5 text-right">
                {p.achievementPct === null ? '—' : `${p.achievementPct}%`}
              </td>
              <td className="px-3 py-1.5 text-right">
                {formatNumber(p.qualifyingSold)}
                {p.passRatePct != null && (
                  <span className="ml-1 text-gray-600">({p.passRatePct}%)</span>
                )}
                {p.casesExcluded > 0 && (
                  <span className="ml-1 text-amber-700">
                    · {p.casesExcluded} of {p.casesTotal} failed
                  </span>
                )}
                {p.casesTotal > 0 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onSeeCases(p); }}
                    className="ml-2 font-medium text-primary-600 underline underline-offset-2"
                  >
                    see which
                  </button>
                )}
              </td>
              <td className="px-3 py-1.5 text-gray-600">{p.bandLabel ?? '—'}</td>
              <td className="px-3 py-1.5 text-right font-medium">
                {p.amount > 0 ? formatMoney(p.amount) : '—'}
              </td>
              <td className="px-3 py-1.5 text-gray-500">{p.note ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One plan's working for the month: the per-unit ladder, or the grid. */
function Working({ products, perUnit, onSeeCases }: {
  products: ProductPayout[]; perUnit?: PerUnitPayout; onSeeCases: (p: ProductPayout) => void;
}) {
  if (perUnit) return <PerUnitWorking products={products} perUnit={perUnit} onSeeCases={onSeeCases} />;
  if (products.length === 0) {
    return (
      <p className="text-xs text-gray-500">
        No per-product working — this plan has no grid to earn from yet.
      </p>
    );
  }
  return <GridWorking products={products} onSeeCases={onSeeCases} />;
}

/** "Mac Plan · 26d" — one plan of a split month, or the days no plan covers. */
const partLabel = (p: IncentivePlanPart): string => `${p.planName ?? 'No plan'} · ${p.days}d`;

/**
 * A month split between plans, on the row: what each plan's days paid, in the
 * Plan column's order. The plan names are in the tooltip and the Plan column.
 */
function SplitLine({ parts }: { parts: IncentivePlanPart[] }) {
  return (
    <div
      className="mt-1 text-xs text-gray-600"
      title={parts.map((p) => `${partLabel(p)} → ${formatMoney(p.paid)}`).join('\n')}
    >
      By days: {parts.map((p) => `${formatMoney(p.paid)} (${p.days}d)`).join(' + ')}
    </div>
  );
}

/**
 * A month split between plans, opened: each plan's working under its own heading.
 * Each plan worked out the WHOLE month — the same targets and sales — and pays the
 * share its days earn; the notes above say so in words. "see which" under a plan
 * opens THAT plan's verdicts, so the list matches the failed count beside it.
 */
function SplitWorking({ row, onSeeCases }: {
  row: IncentiveRunRow; onSeeCases: (planId: string) => (p: ProductPayout) => void;
}) {
  const parts = row.planParts ?? [];
  const totalDays = parts.reduce((n, p) => n + p.days, 0);
  return (
    <div className="space-y-4">
      {parts.map((p) => (
        <div key={p.planId ?? 'none'} className="space-y-1.5">
          <p className="text-xs font-semibold text-gray-800">
            {p.planName ?? 'No plan'} · {p.days} of {totalDays} days
            <span className="font-normal text-gray-500">
              {p.planId === null
                ? ' · earns nothing'
                : ` · ${formatMoney(p.total)} for the month → ${formatMoney(p.paid)} for ${p.days}d`}
            </span>
          </p>
          {p.planId !== null && (
            <Working products={p.products} perUnit={p.perUnit} onSeeCases={onSeeCases(p.planId)} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function IncentivePayout({ projectId, month, planCount }: Props) {
  const [run, setRun] = useState<IncentiveRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [onlyEarners, setOnlyEarners] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [cases, setCases] = useState<{
    employeeCode: string; productId: string; skuId: string | null; label: string; planId: string | null;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await incentiveAPI.run(projectId, month);
      setRun(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || `Could not work out the payout for ${month}.`);
    } finally {
      setLoading(false);
    }
  }, [projectId, month]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, onlyEarners]);

  const rows = useMemo(() => {
    const all = run?.rows ?? [];
    const q = search.trim().toLowerCase();
    return all.filter((r) => {
      if (onlyEarners && r.total <= 0) return false;
      if (!q) return true;
      return r.employeeCode.toLowerCase().includes(q)
        || (r.employeeName ?? '').toLowerCase().includes(q)
        || (r.storeCode ?? '').toLowerCase().includes(q)
        // Any store of the month, not only the one they worked at most.
        || (r.monthStores ?? []).some((m) => (m.storeCode ?? '').toLowerCase().includes(q));
    });
  }, [run, search, onlyEarners]);

  const { totalPages, safePage, paginatedData } = usePagination(rows, page, pageSize);

  // planId: one plan of a split month, so its part's "see which" lists its own verdicts.
  const seeCases = (r: IncentiveRunRow, planId: string | null = null) => (p: ProductPayout) => setCases({
    employeeCode: r.employeeCode,
    productId: p.coord.productId,
    skuId: p.coord.skuId,
    label: p.label,
    planId,
  });

  const toggle = (code: string) => {
    const next = new Set(open);
    if (next.has(code)) next.delete(code); else next.add(code);
    setOpen(next);
  };

  if (loading) {
    return (
      <BlockLoader className="rounded-xl border border-gray-200 bg-white py-20" />
    );
  }
  if (!run) return error ? <PageErrorBanner message={error} /> : null;

  if (planCount === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
        <h3 className="text-base font-medium text-gray-700">No plans, so nothing to pay</h3>
        <p className="mx-auto mt-2 max-w-lg text-sm text-gray-500">
          Add a plan and set how it pays, and this becomes the month&apos;s payout — worked out from
          the targets and sales already in this module.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && <PageErrorBanner message={error} />}

      {/* Paid = Earned + Top-up, laid out left to right so the sum reads off the
          screen. The top-up is its own tile because it was not earned from sales:
          it is what the new-joiner guarantee adds on top of what they sold. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          label="Earned from sales"
          value={formatMoney(run.totals.earned)}
          hint={`${formatNumber(run.totals.payees - run.totals.toppedUp)} promoters earning from sales`}
        />
        <Tile
          label="New joiner top-up"
          value={formatMoney(run.totals.topUp)}
          hint={run.totals.toppedUp > 0
            ? `${formatNumber(run.totals.toppedUp)} of ${formatNumber(run.totals.newJoiners)} new joiners below their guarantee`
            : `${formatNumber(run.totals.newJoiners)} new joiners, none below their guarantee`}
          tone={run.totals.topUp > 0 ? 'warn' : 'plain'}
        />
        <Tile
          label="Total payable"
          value={formatMoney(run.totals.total)}
          hint={`${month} · earned + top-up`}
          tone={run.totals.total > 0 ? 'good' : 'plain'}
        />
        <Tile
          label="Not covered"
          value={formatNumber(run.uncovered.length)}
          tone={run.uncovered.length > 0 ? 'warn' : 'plain'}
          hint={run.uncovered.length > 0 ? 'No plan matches them' : 'Everyone is on a plan'}
        />
      </div>

      {!run.hasCaseDetail && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-xs text-blue-900">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>
            This project records monthly sales totals, so rules about individual sales — a date gap,
            a channel — cannot be applied and every sale counts. Switch the Sales tab to detailed
            sales if you need them.
          </span>
        </div>
      )}

      {run.designationMultiplier && (
        <div className="flex items-start gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-xs text-indigo-900">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span title="What each person earned from sales is multiplied by the figure for their 1Office designation, before the new-joiner guarantee. Not listed = ×1.">
            Designation multiplier from {shortMonth(run.designationMultiplier.effectiveFrom)}:{' '}
            {run.designationMultiplier.rows
              .map((m) => `${m.designation} ${formatMultiplier(m.multiplier)}`).join(' · ')}
            . Earned includes it.
          </span>
        </div>
      )}

      {run.uncovered.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            <strong>{run.uncovered.length}</strong> promoter
            {run.uncovered.length === 1 ? '' : 's'} match no plan and earn nothing:{' '}
            {run.uncovered.slice(0, 6).map((u) => u.employeeName ?? u.employeeCode).join(', ')}
            {run.uncovered.length > 6 && `, and ${run.uncovered.length - 6} more`}. Add a plan with
            no condition to catch everyone else.
          </span>
        </div>
      )}

      {run.plansUsed.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {run.plansUsed.map((p) => (
            <span key={p.id} className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600">
              {p.name}: <strong className="text-gray-900">{formatMoney(p.total)}</strong> · {p.payees} earning
              {p.topUp > 0 && <span className="text-amber-700"> · {formatMoney(p.topUp)} top-up</span>}
            </span>
          ))}
        </div>
      )}

      {/* One row of equal-height controls: a bare checkbox floating beside a
          search box reads as two unrelated things at two different sizes. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-72">
          <SearchInput
            size="md"
            value={search}
            onChange={setSearch}
            placeholder="Search promoter or store"
          />
        </div>
        <label
          className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            onlyEarners
              ? 'border-primary-300 bg-primary-50 font-medium text-primary-700'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          <input
            type="checkbox"
            checked={onlyEarners}
            onChange={(e) => setOnlyEarners(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Only those earning something
        </label>
        <p className="ml-auto text-xs text-gray-500">
          {formatNumber(rows.length)} of {formatNumber(run.rows.length)} promoters shown
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        {/* Scrolls sideways on a narrow screen rather than clipping the money columns. */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="w-8 px-2 py-3" />
                <th className="px-3 py-3">Promoter</th>
                <th className="px-3 py-3">Store</th>
                <th className="px-3 py-3">Plan</th>
                <th className="px-3 py-3">How it was earned</th>
                <th className="px-3 py-3 text-right">Earned</th>
                <th className="px-3 py-3 text-right">New joiner top-up</th>
                <th className="px-3 py-3 text-right">Paid</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedData.map((r: IncentiveRunRow) => {
                const isOpen = open.has(r.employeeCode);
                return (
                  // Fragment carries the key: a bare <> in a map has nowhere to put
                  // one, and React re-creates every expanded row on each render.
                  <Fragment key={r.employeeCode}>
                    <tr
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => toggle(r.employeeCode)}
                    >
                      <td className="px-2 py-2 text-gray-400">
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{r.employeeName ?? r.employeeCode}</div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                          {r.employeeCode}
                          {/* The run pays everyone who worked the month, including leavers. */}
                          <NoLongerDeployedBadge stillDeployed={r.stillDeployed} deployedUntil={r.deployedUntil} />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-gray-600">{storesOf(r)}</td>
                      <td className="px-3 py-2 text-gray-600">
                        {r.planParts
                          ? r.planParts.map((p) => <div key={p.planId ?? 'none'}>{partLabel(p)}</div>)
                          : r.planName}
                        {r.newJoiner && (
                          <span
                            className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800"
                            title={r.guaranteeLost ? guaranteeLostHover(r) : undefined}
                          >
                            New joiner
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {r.perUnit
                          ? <PerUnitVerdict perUnit={r.perUnit} />
                          : <ProductVerdicts products={r.products} />}
                        <MultiplierNote row={r} />
                        {r.planParts && <SplitLine parts={r.planParts} />}
                      </td>
                      <td
                        className="px-3 py-2 text-right text-gray-700"
                        title={r.multiplier !== undefined && r.multiplier !== 1
                          ? `${formatMoney(r.earnedBeforeMultiplier ?? r.earned)} from the plan ${formatMultiplier(r.multiplier)}`
                          : undefined}
                      >
                        {r.earned > 0 ? formatMoney(r.earned) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {/* Coloured only where the guarantee actually added something —
                            the exception, not the rule, so it stands out on a page
                            of dashes. */}
                        {r.topUp > 0
                          ? <span className="font-medium text-amber-700">{formatMoney(r.topUp)}</span>
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className={r.total > 0 ? 'font-semibold text-gray-900' : 'text-gray-400'}>
                          {r.total > 0 ? formatMoney(r.total) : '—'}
                        </span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-50/60">
                        <td />
                        <td colSpan={7} className="px-3 py-3">
                          {r.notes.length > 0 && (
                            <ul className="mb-2 space-y-1">
                              {r.notes.map((n, i) => (
                                <li key={i} className="text-xs text-gray-600">{n}</li>
                              ))}
                            </ul>
                          )}
                          {r.planParts ? (
                            <SplitWorking row={r} onSeeCases={(planId) => seeCases(r, planId)} />
                          ) : (
                            <Working products={r.products} perUnit={r.perUnit} onSeeCases={seeCases(r)} />
                          )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {paginatedData.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">
                  {run.rows.length === 0
                    ? 'No promoters are deployed in this project for this month.'
                    : 'Nothing matches that filter.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
        {rows.length > 0 && (
          <PaginationFooter
            safePage={safePage}
            totalPages={totalPages}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZES}
            totalRows={rows.length}
            onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
            onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
            onNextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
          />
        )}
      </div>

      <SalesCasesModal
        isOpen={cases !== null}
        onClose={() => setCases(null)}
        projectId={projectId}
        month={month}
        employeeCode={cases?.employeeCode ?? null}
        productId={cases?.productId ?? null}
        skuId={cases?.skuId ?? null}
        productLabel={cases?.label ?? null}
        planId={cases?.planId ?? null}
      />
    </div>
  );
}
